import React, { useState, useRef, useCallback } from 'react';
import { SendIcon, ImageIcon, MicIcon } from './Icons';

interface ChatInputProps {
  onSendMessage: (content: string, attachments?: File[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isLoading = false, disabled = false }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = message.trim();
    if (!trimmed && attachments.length === 0) return;
    if (isLoading || disabled) return;

    onSendMessage(trimmed, attachments.length > 0 ? attachments : undefined);
    setMessage('');
    setAttachments([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [message, attachments, isLoading, disabled, onSendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments((prev) => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleMicClick = () => {
    setIsRecording(!isRecording);
    // Voice recording would be implemented via a hook/service
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const containerStyle: React.CSSProperties = {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    padding: '12px 16px',
    backgroundColor: '#2B3B44',
  };

  const attachmentsRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '6px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  };

  const attachmentChipStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#C8D6DF',
  };

  const removeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    color: '#FF5656',
    cursor: 'pointer',
    padding: '0 2px',
    fontSize: '14px',
    lineHeight: 1,
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
  };

  const inputWrapperStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    backgroundColor: '#30424D',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
    padding: '8px 12px',
    transition: 'border-color 0.15s ease',
  };

  const textareaStyle: React.CSSProperties = {
    flex: 1,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#ffffff',
    fontSize: '14px',
    lineHeight: '1.4',
    resize: 'none',
    maxHeight: '120px',
    fontFamily: 'inherit',
  };

  const actionButtonStyle = (active?: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    background: active ? 'rgba(255,86,86,0.2)' : 'transparent',
    cursor: 'pointer',
    padding: 0,
    color: active ? '#FF5656' : '#8899A6',
    transition: 'color 0.15s ease',
  });

  const sendButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: message.trim() || attachments.length > 0 ? '#33AA40' : '#394E5B',
    cursor: message.trim() || attachments.length > 0 ? 'pointer' : 'default',
    padding: 0,
    transition: 'background-color 0.15s ease',
    opacity: isLoading ? 0.6 : 1,
  };

  return (
    <div style={containerStyle}>
      {attachments.length > 0 && (
        <div style={attachmentsRowStyle}>
          {attachments.map((file, index) => (
            <div key={index} style={attachmentChipStyle}>
              <span>{file.name.length > 15 ? file.name.slice(0, 12) + '...' : file.name}</span>
              <button
                style={removeButtonStyle}
                onClick={() => removeAttachment(index)}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <form style={formStyle} onSubmit={handleSubmit}>
        <button
          type="button"
          style={actionButtonStyle()}
          onClick={handleImageClick}
          title="Attach image"
        >
          <ImageIcon size={18} color="#8899A6" />
        </button>
        <button
          type="button"
          style={actionButtonStyle(isRecording)}
          onClick={handleMicClick}
          title={isRecording ? 'Stop recording' : 'Voice input'}
        >
          <MicIcon size={18} color={isRecording ? '#FF5656' : '#8899A6'} />
        </button>
        <div style={inputWrapperStyle}>
          <textarea
            ref={textareaRef}
            style={textareaStyle}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask Equanaut..."
            rows={1}
            disabled={disabled}
          />
        </div>
        <button
          type="submit"
          style={sendButtonStyle}
          disabled={isLoading || (!message.trim() && attachments.length === 0)}
          title="Send message"
        >
          <SendIcon size={18} color="#ffffff" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </form>
    </div>
  );
};

export default ChatInput;
